import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import {
  LayoutDashboard, Globe, GraduationCap, FileText, MessageSquare, Settings, UserCircle, LogOut,
  Loader2, Inbox, MessageCircle, PhoneCall, Mail, MapPin, CalendarClock, ShieldCheck, KeyRound, Save,
} from "lucide-react";
import { toast } from "sonner";
import { LOGO_URL } from "@/data/content";
import { PasswordInput, adminInputCls, adminLabelCls, formatApiError, passwordStrength } from "@/components/admin-ui";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUSES = ["new", "contacted", "in-progress", "closed"];
const STATUS_STYLES = {
  new: "bg-gold/10 text-navy border-gold/40",
  contacted: "bg-blue-50 text-blue-700 border-blue-200",
  "in-progress": "bg-amber-50 text-amber-700 border-amber-200",
  closed: "bg-emerald-50 text-emerald-700 border-emerald-200",
};
const NAV = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { id: "content", label: "Website Content", icon: Globe },
  { id: "students", label: "Students", icon: GraduationCap },
  { id: "applications", label: "Applications", icon: FileText },
  { id: "messages", label: "Messages", icon: MessageSquare },
  { id: "settings", label: "Settings", icon: Settings },
  { id: "profile", label: "Admin Profile", icon: UserCircle },
];

const waNumber = (phone) => {
  const digits = (phone || "").replace(/\D/g, "");
  return digits.length === 10 ? `91${digits}` : digits;
};

const EmptyModule = ({ label }) => (
  <div className="flex flex-col items-center rounded-xl border border-dashed border-line bg-white py-24 text-center" data-testid={`module-${label.toLowerCase().replace(/\s/g, "-")}`}>
    <Inbox className="h-12 w-12 text-slate-300" />
    <h3 className="mt-4 font-heading text-xl font-bold text-navy">{label}</h3>
    <p className="mt-2 max-w-sm text-sm leading-relaxed text-muted">
      This module isn't connected yet. The admin framework is ready — functionality can be wired in here when you need it.
    </p>
  </div>
);

const MessagesSection = ({ enquiries, onStatus }) => (
  <div className="space-y-5" data-testid="messages-section">
    {enquiries.length === 0 ? (
      <EmptyModule label="No messages yet" />
    ) : (
      enquiries.map((e, i) => (
        <article key={e.id} data-testid={`enquiry-card-${i}`} className="rounded-xl border border-line bg-white p-6 shadow-soft">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h3 className="font-heading text-lg font-bold text-navy">{e.name}</h3>
              <p className="mt-1 flex items-center gap-2 text-xs text-muted">
                <CalendarClock className="h-3.5 w-3.5 text-gold" />
                {e.created_at ? new Date(e.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : ""}
              </p>
            </div>
            <span className={`rounded-full border px-3.5 py-1 font-accent text-[11px] font-bold uppercase tracking-widest ${STATUS_STYLES[e.status || "new"]}`} data-testid={`enquiry-status-${i}`}>
              {e.status || "new"}
            </span>
          </div>
          <div className="mt-4 grid grid-cols-1 gap-2.5 text-sm text-muted sm:grid-cols-2">
            <span className="flex items-center gap-2"><GraduationCap className="h-4 w-4 shrink-0 text-gold" /> {e.country} · {e.visa_type}</span>
            <span className="flex items-center gap-2"><MapPin className="h-4 w-4 shrink-0 text-gold" /> {e.location} · {e.qualification}</span>
            <span className="flex items-center gap-2 sm:col-span-2"><Mail className="h-4 w-4 shrink-0 text-gold" /> <span className="break-all">{e.email}</span></span>
          </div>
          {e.notes && <p className="mt-4 rounded-lg bg-surface px-4 py-3 text-sm italic text-muted">"{e.notes}"</p>}
          <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-line pt-4">
            <a href={`https://wa.me/${waNumber(e.phone)}`} target="_blank" rel="noopener noreferrer" data-testid={`enquiry-whatsapp-${i}`}
              className="flex items-center gap-2 rounded-full bg-navy px-4 py-2 font-accent text-[11px] font-bold uppercase tracking-widest text-white transition-colors hover:bg-gold">
              <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
            </a>
            <a href={`tel:+${waNumber(e.phone)}`} data-testid={`enquiry-call-${i}`}
              className="flex items-center gap-2 rounded-full border border-line px-4 py-2 font-accent text-[11px] font-semibold uppercase tracking-widest text-navy transition-colors hover:border-gold hover:text-gold">
              <PhoneCall className="h-3.5 w-3.5" /> Call {e.phone}
            </a>
            <select
              aria-label="Follow-up status"
              data-testid={`status-select-${i}`}
              value={e.status || "new"}
              onChange={(ev) => onStatus(e.id, ev.target.value)}
              className="ml-auto rounded-lg border border-line bg-white px-3 py-2 text-sm text-navy focus:border-gold focus:outline-none"
            >
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </article>
      ))
    )}
  </div>
);

const ProfileSection = ({ user, onUser }) => {
  const [username, setUsername] = useState(user.username);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [emailCode, setEmailCode] = useState("");
  const [emailStep, setEmailStep] = useState(1);

  const saveUsername = async () => {
    setSaving(true);
    try {
      const { data } = await axios.patch(`${API}/auth/profile`, { username }, { withCredentials: true });
      onUser(data);
      toast.success("Username updated");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const requestEmailChange = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/auth/request-email-change`, { new_email: newEmail }, { withCredentials: true });
      setEmailStep(2);
      toast.success("Verification code sent to the new email");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const confirmEmailChange = async () => {
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/auth/confirm-email-change`, { code: emailCode }, { withCredentials: true });
      onUser(data);
      setEmailStep(1);
      setNewEmail("");
      setEmailCode("");
      toast.success("Admin email updated");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (next !== confirm) return toast.error("New passwords do not match.");
    if (passwordStrength(next) < 5) return toast.error("Password must be 12+ chars with upper, lower, number and symbol.");
    setSaving(true);
    try {
      await axios.post(`${API}/auth/change-password`, { current_password: current, new_password: next }, { withCredentials: true });
      toast.success("Password changed — other sessions signed out");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-2" data-testid="profile-section">
      <div className="rounded-xl border border-line bg-white p-8 shadow-soft">
        <h3 className="flex items-center gap-2 font-heading text-lg font-bold text-navy"><UserCircle className="h-5 w-5 text-gold" /> Account</h3>
        <div className="mt-6 space-y-5">
          <div>
            <p className={adminLabelCls}>Registered Email</p>
            <p className="rounded-lg bg-surface px-4 py-3 text-base text-navy" data-testid="profile-email">{user.email}</p>
          </div>
          <div className="rounded-xl border border-line p-5">
            <p className={adminLabelCls}>Change Email (verified)</p>
            {emailStep === 1 ? (
              <div className="space-y-3">
                <input data-testid="profile-new-email" type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="New email address" className={adminInputCls} />
                <button onClick={requestEmailChange} disabled={saving || !newEmail.includes("@")} data-testid="profile-send-email-code"
                  className="flex items-center gap-2 rounded-full border border-navy px-5 py-2.5 font-accent text-xs font-bold uppercase tracking-widest text-navy transition-colors hover:bg-navy hover:text-white disabled:opacity-50">
                  <Mail className="h-4 w-4" /> Send Verification Code
                </button>
                <p className="text-xs text-muted">A code is sent to the new address — the change only happens after verification.</p>
              </div>
            ) : (
              <div className="space-y-3">
                <input data-testid="profile-email-code" inputMode="numeric" maxLength={6} value={emailCode} onChange={(e) => setEmailCode(e.target.value.replace(/\D/g, ""))} placeholder="6-digit code" className={`${adminInputCls} text-center tracking-[0.4em]`} />
                <div className="flex gap-3">
                  <button onClick={confirmEmailChange} disabled={saving || emailCode.length !== 6} data-testid="profile-confirm-email"
                    className="flex items-center gap-2 rounded-full bg-navy px-5 py-2.5 font-accent text-xs font-bold uppercase tracking-widest text-white transition-colors hover:bg-gold disabled:opacity-50">
                    <ShieldCheck className="h-4 w-4" /> Confirm Change
                  </button>
                  <button onClick={() => { setEmailStep(1); setEmailCode(""); }} data-testid="profile-cancel-email"
                    className="rounded-full border border-line px-5 py-2.5 font-accent text-xs font-semibold uppercase tracking-widest text-muted transition-colors hover:text-navy">
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
          <div>
            <label htmlFor="profile-username" className={adminLabelCls}>Username</label>
            <input id="profile-username" data-testid="profile-username" value={username} onChange={(e) => setUsername(e.target.value.toLowerCase())} className={adminInputCls} />
            <p className="mt-1.5 text-xs text-muted">3–30 characters: lowercase letters, numbers, underscores.</p>
          </div>
          <button onClick={saveUsername} disabled={saving || username === user.username} data-testid="profile-save-username"
            className="flex items-center gap-2 rounded-full bg-navy px-6 py-3 font-accent text-xs font-bold uppercase tracking-widest text-white transition-colors hover:bg-gold disabled:opacity-50">
            <Save className="h-4 w-4" /> Save Username
          </button>
        </div>
      </div>
      <div className="rounded-xl border border-line bg-white p-8 shadow-soft">
        <h3 className="flex items-center gap-2 font-heading text-lg font-bold text-navy"><KeyRound className="h-5 w-5 text-gold" /> Change Password</h3>
        <form onSubmit={changePassword} className="mt-6 space-y-5">
          <PasswordInput label="Current Password" testid="profile-current-password" value={current} onChange={(e) => setCurrent(e.target.value)} />
          <PasswordInput label="New Password" testid="profile-new-password" value={next} onChange={(e) => setNext(e.target.value)} showStrength />
          <PasswordInput label="Confirm New Password" testid="profile-confirm-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
          <button type="submit" disabled={saving} data-testid="profile-change-password"
            className="flex items-center gap-2 rounded-full bg-navy px-6 py-3 font-accent text-xs font-bold uppercase tracking-widest text-white transition-colors hover:bg-gold disabled:opacity-50">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />} Update Password
          </button>
          <p className="text-xs leading-relaxed text-muted">Changing your password signs out all other sessions.</p>
        </form>
      </div>
    </div>
  );
};

export default function AdminDashboard() {
  const [user, setUser] = useState(null);
  const [enquiries, setEnquiries] = useState([]);
  const [section, setSection] = useState("dashboard");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const me = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(me.data);
      const { data } = await axios.get(`${API}/enquiries`, { withCredentials: true });
      setEnquiries(data);
    } catch {
      navigate("/admin", { replace: true });
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/enquiries/${id}`, { status }, { withCredentials: true });
      setEnquiries((list) => list.map((e) => (e.id === id ? { ...e, status } : e)));
      toast.success(`Marked as ${status}`);
    } catch {
      toast.error("Could not update status");
    }
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    navigate("/admin", { replace: true });
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface" data-testid="admin-loading">
        <Loader2 className="h-8 w-8 animate-spin text-gold" />
      </div>
    );
  }

  const counts = STATUSES.reduce((acc, s) => ({ ...acc, [s]: enquiries.filter((e) => (e.status || "new") === s).length }), {});

  return (
    <div className="min-h-screen bg-surface" data-testid="admin-dashboard">
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 flex-col border-r border-line bg-navy text-white lg:flex" data-testid="admin-sidebar">
        <div className="flex items-center gap-3 border-b border-white/10 px-6 py-6">
          <span className="flex items-center justify-center rounded-xl bg-white px-2.5 py-1.5">
            <img src={LOGO_URL} alt="Student First Overseas" className="h-8 w-auto" />
          </span>
          <div>
            <p className="font-heading text-sm font-extrabold leading-tight">Student First</p>
            <p className="font-accent text-[10px] font-semibold uppercase tracking-widest text-gold">Admin Dashboard</p>
          </div>
        </div>
        <nav className="flex-1 space-y-1 overflow-y-auto px-4 py-6" aria-label="Admin navigation">
          {NAV.map((n) => (
            <button
              key={n.id}
              onClick={() => setSection(n.id)}
              data-testid={`admin-nav-${n.id}`}
              aria-current={section === n.id ? "page" : undefined}
              className={`flex w-full items-center gap-3 rounded-lg px-4 py-3 text-left font-accent text-xs font-semibold uppercase tracking-widest transition-colors ${
                section === n.id ? "bg-gold text-white" : "text-slate-300 hover:bg-white/5 hover:text-white"
              }`}
            >
              <n.icon className="h-4 w-4" /> {n.label}
            </button>
          ))}
        </nav>
        <div className="border-t border-white/10 p-4">
          <p className="mb-3 truncate px-2 text-xs text-slate-400">{user?.email}</p>
          <button onClick={logout} data-testid="admin-logout-button"
            className="flex w-full items-center gap-3 rounded-lg px-4 py-3 font-accent text-xs font-semibold uppercase tracking-widest text-slate-300 transition-colors hover:bg-gold hover:text-white">
            <LogOut className="h-4 w-4" /> Logout
          </button>
        </div>
      </aside>

      <div className="sticky top-0 z-40 flex items-center justify-between border-b border-line bg-navy px-5 py-3.5 text-white lg:hidden">
        <div className="flex items-center gap-2.5">
          <img src={LOGO_URL} alt="" className="h-8 w-auto rounded-md bg-white px-1.5 py-0.5" />
          <span className="font-heading text-sm font-extrabold">Admin <span className="text-gold">Dashboard</span></span>
        </div>
        <button onClick={logout} data-testid="admin-logout-button-mobile" aria-label="Logout" className="rounded-full border border-white/20 p-2.5 text-slate-300">
          <LogOut className="h-4 w-4" />
        </button>
      </div>
      <nav className="sticky top-[60px] z-30 flex gap-1 overflow-x-auto border-b border-line bg-white px-4 py-2 lg:hidden" aria-label="Admin navigation mobile">
        {NAV.map((n) => (
          <button key={n.id} onClick={() => setSection(n.id)} data-testid={`admin-nav-mobile-${n.id}`}
            className={`flex shrink-0 items-center gap-2 rounded-full px-4 py-2 font-accent text-[11px] font-semibold uppercase tracking-widest ${section === n.id ? "bg-navy text-white" : "text-muted"}`}>
            <n.icon className="h-3.5 w-3.5" /> {n.label}
          </button>
        ))}
      </nav>

      <main className="px-5 py-8 lg:ml-64 lg:px-10 lg:py-10">
        <motion.div key={section} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
          <h2 className="font-heading text-2xl font-extrabold text-navy sm:text-3xl" data-testid="admin-section-title">
            {NAV.find((n) => n.id === section)?.label}
          </h2>
          <div className="mt-8">
            {section === "dashboard" && (
              <div data-testid="dashboard-section">
                <div className="grid grid-cols-2 gap-4 xl:grid-cols-4">
                  {STATUSES.map((s) => (
                    <div key={s} className="rounded-xl border border-line bg-white p-6 shadow-soft" data-testid={`stat-${s}`}>
                      <p className="font-heading text-3xl font-extrabold text-navy">{counts[s]}</p>
                      <p className="mt-1 font-accent text-[11px] font-semibold uppercase tracking-widest text-muted">{s} enquiries</p>
                    </div>
                  ))}
                </div>
                <div className="mt-8 rounded-xl border border-line bg-white p-8 shadow-soft">
                  <h3 className="font-heading text-lg font-bold text-navy">Latest messages</h3>
                  {enquiries.length === 0 ? (
                    <p className="mt-4 text-sm text-muted">No enquiries yet — new consultation form submissions appear here instantly.</p>
                  ) : (
                    <ul className="mt-5 divide-y divide-line">
                      {enquiries.slice(0, 5).map((e, i) => (
                        <li key={e.id} className="flex flex-wrap items-center justify-between gap-3 py-4" data-testid={`recent-enquiry-${i}`}>
                          <div>
                            <p className="font-heading text-sm font-bold text-navy">{e.name} <span className="ml-2 font-body text-xs font-normal text-muted">{e.country} · {e.visa_type}</span></p>
                            <p className="text-xs text-muted">{e.created_at ? new Date(e.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : ""}</p>
                          </div>
                          <span className={`rounded-full border px-3 py-1 font-accent text-[10px] font-bold uppercase tracking-widest ${STATUS_STYLES[e.status || "new"]}`}>{e.status || "new"}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {enquiries.length > 0 && (
                    <button onClick={() => setSection("messages")} data-testid="view-all-messages" className="mt-5 font-accent text-xs font-bold uppercase tracking-widest text-gold transition-colors hover:text-navy">
                      View all messages →
                    </button>
                  )}
                </div>
              </div>
            )}
            {section === "messages" && <MessagesSection enquiries={enquiries} onStatus={setStatus} />}
            {section === "profile" && <ProfileSection user={user} onUser={setUser} />}
            {["content", "students", "applications", "settings"].includes(section) && (
              <EmptyModule label={NAV.find((n) => n.id === section).label} />
            )}
          </div>
        </motion.div>
      </main>
    </div>
  );
}
